import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Badge, Button, Col, Form, Modal, Row, Stack } from 'react-bootstrap'
import { ArrowLeft } from 'lucide-react'
import {
  addProductAttribute,
  deleteProductAttribute,
  fetchMasterTerms,
  fetchProductComparisonTable,
  fetchProducts,
  fetchTermReferenceTable,
  importMasterTermToTermReference,
  setKingspanCanonicalValue,
  setProductApproval,
  syncProductAttributesToMasterData,
  type MasterTermDto,
  type ProductComparisonRowDto,
  type ProductDto,
  type TermReferenceRowDto,
} from '../services/api'
import { useToast } from '../components/ToastProvider'
import VariantMatrixEditor from '../components/VariantMatrixEditor'
import { KpiCard, MimCard, MimPage, StatusPill } from '../components/MimUi'
import '../styles/pageShellCompact.css'
import './ProductDetail.css'

type AttributeDraft = {
  value: string
  unit: string
}

type DirectAttributeForm = {
  canonicalTerm: string
  category: string
  dataType: string
  value: string
  unit: string
  compareMode: 'Exact' | 'Pattern' | 'Semantic'
  useAiSecondOpinion: boolean
}

const EMPTY_DIRECT_ATTRIBUTE: DirectAttributeForm = {
  canonicalTerm: '',
  category: 'Technical',
  dataType: 'Text',
  value: '',
  unit: '',
  compareMode: 'Exact',
  useAiSecondOpinion: false,
}

const dataGroups = ['Technical', 'Marketing', 'Metadata', 'Downloads', 'Dimensions']
const dataTypes = ['Text', 'Number', 'Boolean', 'Url', 'Date']

export default function ProductDetail() {
  const { productId } = useParams<{ productId: string }>()
  const navigate = useNavigate()
  const { showToast } = useToast()
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [draftValues, setDraftValues] = useState<Record<string, AttributeDraft>>({})
  const [message, setMessage] = useState('')
  const [showFamilyModal, setShowFamilyModal] = useState(false)
  const [showDirectModal, setShowDirectModal] = useState(false)
  const [selectedMasterTermIds, setSelectedMasterTermIds] = useState<string[]>([])
  const [masterTermSearch, setMasterTermSearch] = useState('')
  const [directForm, setDirectForm] = useState<DirectAttributeForm>(EMPTY_DIRECT_ATTRIBUTE)

  const productsQuery = useQuery<ProductDto[]>({
    queryKey: ['products-for-detail'],
    queryFn: () => fetchProducts(),
  })

  const product = useMemo(
    () => productsQuery.data?.find((item) => item.id === productId) ?? null,
    [productId, productsQuery.data],
  )

  const comparisonQuery = useQuery<ProductComparisonRowDto[]>({
    enabled: !!productId,
    queryKey: ['product-detail-comparison', productId],
    queryFn: () => fetchProductComparisonTable(productId!, undefined, false),
  })

  const termReferenceQuery = useQuery<TermReferenceRowDto[]>({
    enabled: !!product?.family,
    queryKey: ['product-detail-term-reference', product?.family],
    queryFn: () => fetchTermReferenceTable({ productFamily: product!.family || undefined }),
  })

  const masterTermsQuery = useQuery<MasterTermDto[]>({
    queryKey: ['product-detail-master-terms'],
    queryFn: () => fetchMasterTerms(),
  })

  const rows = comparisonQuery.data ?? []
  const termReference = termReferenceQuery.data ?? []
  const masterTerms = masterTermsQuery.data ?? []
  const productFamily = product?.family?.trim() || '*'

  useEffect(() => {
    const next: Record<string, AttributeDraft> = {}
    for (const row of rows) {
      next[row.canonicalAttributeId] = {
        value: row.kingspanValue ?? '',
        unit: row.canonicalUnit ?? '',
      }
    }
    setDraftValues(next)
  }, [rows])

  const categories = useMemo(() => (
    Array.from(new Set(rows.map((row) => row.category).filter(Boolean))).sort()
  ), [rows])

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return rows.filter((row) => {
      if (categoryFilter !== 'all' && row.category !== categoryFilter) return false
      if (!query) return true
      const blob = `${row.displayLabel} ${row.attributeName} ${row.category} ${row.kingspanValue ?? ''}`.toLowerCase()
      return blob.includes(query)
    })
  }, [categoryFilter, rows, search])

  const availableMasterTerms = useMemo(() => {
    const assigned = new Set(termReference.map((row) => row.canonicalTerm.toLowerCase()))
    const query = masterTermSearch.trim().toLowerCase()
    return masterTerms
      .filter((term) => term.status !== 'Retired')
      .filter((term) => !assigned.has(term.canonicalTerm.toLowerCase()))
      .filter((term) => {
        if (!query) return true
        const blob = `${term.canonicalTerm} ${term.category} ${term.aliases.map((alias) => alias.alias).join(' ')}`.toLowerCase()
        return blob.includes(query)
      })
  }, [masterTermSearch, masterTerms, termReference])

  const populatedCount = rows.filter((row) => row.kingspanStatus === 'Present').length
  const missingCount = Math.max(rows.length - populatedCount, 0)

  const saveValueMutation = useMutation({
    mutationFn: async (row: ProductComparisonRowDto) => {
      const draft = draftValues[row.canonicalAttributeId] ?? { value: '', unit: '' }
      await setKingspanCanonicalValue(productId!, row.canonicalAttributeId, {
        value: draft.value,
        unit: draft.unit || undefined,
        actor: 'product-detail-ui',
      })
    },
    onSuccess: async () => {
      setMessage('Attribute value saved.')
      showToast('Attribute value saved.', { title: 'Product details', variant: 'success' })
      await comparisonQuery.refetch()
    },
    onError: (error: any) => {
      const text = error.response?.data ?? error.message ?? 'Failed to save attribute value.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const approveMutation = useMutation({
    mutationFn: (nextApproved: boolean) => setProductApproval(productId!, nextApproved),
    onSuccess: async (_data, nextApproved) => {
      setMessage(nextApproved ? 'Product approved.' : 'Product approval revoked.')
      showToast(nextApproved ? 'Product approved.' : 'Product approval revoked.', { title: 'Product details', variant: 'success' })
      await productsQuery.refetch()
    },
    onError: (error: any) => {
      const text = error.response?.status === 403
        ? 'You do not have permission to approve products.'
        : error.response?.data ?? error.message ?? 'Failed to update approval status.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const addFamilyAttributesMutation = useMutation({
    mutationFn: async () => {
      const selected = masterTerms.filter((term) => selectedMasterTermIds.includes(term.id))
      for (const term of selected) {
        await importMasterTermToTermReference({
          masterTermId: term.id,
          productFamily,
          category: term.category,
          includeTranslationsAsAliases: true,
          compareMode: 'Exact',
          useAiSecondOpinion: false,
          applyScope: true,
          actor: 'product-detail-ui',
        })
      }
      return selected.length
    },
    onSuccess: async (count) => {
      setShowFamilyModal(false)
      setSelectedMasterTermIds([])
      setMessage(`Added ${count} attribute${count === 1 ? '' : 's'} to ${productFamily}.`)
      showToast('Family attributes added.', { title: 'Product details', variant: 'success' })
      await Promise.all([
        termReferenceQuery.refetch(),
        comparisonQuery.refetch(),
      ])
    },
    onError: (error: any) => {
      const text = error.response?.data ?? error.message ?? 'Failed to add family attributes.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const addDirectAttributeMutation = useMutation({
    mutationFn: () => addProductAttribute(productId!, {
      canonicalTerm: directForm.canonicalTerm.trim(),
      category: directForm.category,
      dataType: directForm.dataType,
      value: directForm.value.trim(),
      unit: directForm.unit.trim() || undefined,
      compareMode: directForm.compareMode,
      useAiSecondOpinion: directForm.compareMode === 'Semantic' || directForm.useAiSecondOpinion,
      actor: 'product-detail-ui',
    }),
    onSuccess: async () => {
      setShowDirectModal(false)
      setDirectForm(EMPTY_DIRECT_ATTRIBUTE)
      setMessage('Product attribute added.')
      showToast('Product attribute added.', { title: 'Product details', variant: 'success' })
      await Promise.all([
        termReferenceQuery.refetch(),
        comparisonQuery.refetch(),
      ])
    },
    onError: (error: any) => {
      const text = error.response?.data ?? error.message ?? 'Failed to add product attribute.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const syncAttributesMutation = useMutation({
    mutationFn: () => syncProductAttributesToMasterData(productId!, { actor: 'product-detail-ui' }),
    onSuccess: async (result) => {
      const text = `${result.message} Created ${result.masterTermsCreated} master term${result.masterTermsCreated === 1 ? '' : 's'} and ${result.familyLinksCreated} family link${result.familyLinksCreated === 1 ? '' : 's'}.`
      setMessage(text)
      showToast('Attributes synced to master data.', { title: 'Product details', variant: 'success' })
      await Promise.all([
        masterTermsQuery.refetch(),
        termReferenceQuery.refetch(),
        comparisonQuery.refetch(),
      ])
    },
    onError: (error: any) => {
      const text = error.response?.data ?? error.message ?? 'Failed to sync attributes.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const deleteAttributeMutation = useMutation({
    mutationFn: ({ canonicalAttributeId, deleteFromFamily }: { canonicalAttributeId: string; deleteFromFamily: boolean }) =>
      deleteProductAttribute(productId!, canonicalAttributeId, deleteFromFamily),
    onSuccess: async (result) => {
      setMessage(result.message)
      showToast(result.message, { title: 'Product details', variant: 'success' })
      await Promise.all([
        termReferenceQuery.refetch(),
        comparisonQuery.refetch(),
      ])
    },
    onError: (error: any) => {
      const text = error.response?.data ?? error.message ?? 'Failed to delete attribute.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    },
  })

  const toggleMasterTerm = (id: string) => {
    setSelectedMasterTermIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )
  }

  const saveAll = async () => {
    setMessage('')
    try {
      for (const row of filteredRows) {
        const draft = draftValues[row.canonicalAttributeId]
        if (!draft?.value.trim()) continue
        await setKingspanCanonicalValue(productId!, row.canonicalAttributeId, {
          value: draft.value,
          unit: draft.unit || undefined,
          actor: 'product-detail-ui',
        })
      }
      setMessage('Saved all filled values in the current view.')
      showToast('Saved all filled values.', { title: 'Product details', variant: 'success' })
      await comparisonQuery.refetch()
    } catch (error: any) {
      const text = error.response?.data ?? error.message ?? 'Failed to save all values.'
      setMessage(text)
      showToast(text, { title: 'Product details', variant: 'danger' })
    }
  }

  const requestDeleteAttribute = (row: ProductComparisonRowDto) => {
    const deleteFromProduct = window.confirm(`Remove "${row.displayLabel}" from this product?`)
    if (!deleteFromProduct) return
    const deleteFromFamily = window.confirm(`Also remove "${row.displayLabel}" from the ${productFamily} product family? This will hide it from other products in the same family.`)
    deleteAttributeMutation.mutate({ canonicalAttributeId: row.canonicalAttributeId, deleteFromFamily })
  }

  const isLoading = productsQuery.isLoading || comparisonQuery.isLoading
  const productsErrorText = productsQuery.isError
    ? (productsQuery.error as any)?.response?.data?.message
      ?? (typeof (productsQuery.error as any)?.response?.data === 'string'
        ? (productsQuery.error as any).response.data
        : null)
      ?? (productsQuery.error as Error)?.message
      ?? 'Request failed'
    : null
  const comparisonErrorText = comparisonQuery.isError
    ? (comparisonQuery.error as any)?.response?.data?.message
      ?? (typeof (comparisonQuery.error as any)?.response?.data === 'string'
        ? (comparisonQuery.error as any).response.data
        : null)
      ?? (comparisonQuery.error as Error)?.message
      ?? 'Request failed'
    : null

  return (
    <MimPage
      title={product ? `${product.code} — ${product.name}` : 'Product details'}
      subtitle="Source-of-truth attributes and variant matrix"
      compactHeader
      actions={
        <button type="button" className="mim-btn mim-btn-secondary" onClick={() => navigate('/products')}>
          <ArrowLeft size={15} />
          Back to PCR management
        </button>
      }
    >
    <div className="product-detail-page mim-stack">
      {product && (
        <div className="mim-toolbar" style={{ justifyContent: 'flex-end' }}>
          <StatusPill status={product.family ? `Family: ${product.family}` : 'No family'} tone="neutral" />
          <StatusPill
            status={product.hasKingspanSource ? 'Source of truth set' : 'Source of truth missing'}
            tone={product.hasKingspanSource ? 'pass' : 'pending'}
          />
          <StatusPill status={product.isApproved ? 'Approved' : 'Not approved'} tone={product.isApproved ? 'pass' : 'warning'} />
          <button
            type="button"
            className={`mim-btn ${product.isApproved ? 'mim-btn-secondary' : 'mim-btn-primary'}`}
            disabled={approveMutation.isPending}
            onClick={() => approveMutation.mutate(!product.isApproved)}
          >
            {approveMutation.isPending ? 'Saving...' : product.isApproved ? 'Unapprove' : 'Approve'}
          </button>
          <Link className="mim-btn mim-btn-primary" to={`/compliance-check?productId=${product.id}`}>Create a check</Link>
        </div>
      )}

      {message && (
        <div className="mim-note" style={message.toLowerCase().includes('failed') ? { background: 'var(--mim-danger-bg)', color: 'var(--mim-danger)' } : undefined}>
          {message}
        </div>
      )}

      {productsQuery.isError && (
        <Alert variant="danger" className="py-2">
          <div className="fw-semibold mb-1">Could not load products</div>
          <div className="small mb-0">{productsErrorText}</div>
        </Alert>
      )}

      {product && comparisonQuery.isError && (
        <Alert variant="danger" className="py-2">
          <div className="fw-semibold mb-1">Could not load product attributes (comparison API)</div>
          <div className="small mb-2">{comparisonErrorText}</div>
          <Button size="sm" variant="outline-danger" onClick={() => void comparisonQuery.refetch()}>
            Retry
          </Button>
        </Alert>
      )}

      {isLoading && <div className="mim-note">Loading product details...</div>}
      {!isLoading && !productsQuery.isError && !product && (
        <Alert variant="danger">
          Product not found in your library list. If the product was recently added, refresh the page or open it again from the product library.
        </Alert>
      )}

      {product && (
        <div className="product-detail-main mim-stack">
          <div className="mim-grid-4">
            <KpiCard compact label="Attributes" value={rows.length} />
            <KpiCard compact label="Values completed" value={populatedCount} tone="green" />
            <KpiCard compact label="Missing values" value={missingCount} tone="orange" />
            <KpiCard
              compact
              label="Kingspan URL"
              value={product.kingspanUrl ? 'Set' : 'Not set'}
              detail={product.kingspanUrl ? (
                <a href={product.kingspanUrl} target="_blank" rel="noreferrer">Open source page</a>
              ) : undefined}
            />
          </div>

          <MimCard title="Product attributes" subtitle="Edit Kingspan product data. Distributor comparison is on the compare page."
            actions={<Link className="mim-btn mim-btn-secondary" to={`/products/${product.id}/compare`}>Open comparison</Link>}
          >
            <div className="mim-toolbar" style={{ marginBottom: 12 }}>
              <div className="mim-search" style={{ flex: '1 1 220px' }}>
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search attributes..."
                />
              </div>
              <select className="mim-select" value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} style={{ minWidth: 140 }}>
                <option value="all">All groups</option>
                {categories.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => syncAttributesMutation.mutate()} disabled={syncAttributesMutation.isPending}>
                {syncAttributesMutation.isPending ? 'Syncing...' : 'Sync to master data'}
              </button>
              <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setShowFamilyModal(true)}>Add family attributes</button>
              <button type="button" className="mim-btn mim-btn-primary" onClick={() => setShowDirectModal(true)}>Add product attribute</button>
              <button type="button" className="mim-btn mim-btn-secondary" onClick={saveAll} disabled={saveValueMutation.isPending}>Save all shown</button>
            </div>

            {filteredRows.length === 0 ? (
              <div className="mim-note">No attributes match this view. Add family attributes or a product attribute to get started.</div>
            ) : (
              <div className="product-detail-table-scroll mim-table-wrap">
                <table className="mim-table">
                  <thead>
                    <tr>
                      <th>Attribute</th>
                      <th>Current value</th>
                      <th>Unit</th>
                      <th>Compare</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const draft = draftValues[row.canonicalAttributeId] ?? { value: '', unit: '' }
                      return (
                        <tr key={row.canonicalAttributeId}>
                          <td>
                            <strong>{row.displayLabel}</strong>
                            <div className="mim-help-text">{row.attributeName} · {row.category}</div>
                          </td>
                          <td>
                            <input
                              className="mim-input"
                              value={draft.value}
                              onChange={(event) => setDraftValues((current) => ({
                                ...current,
                                [row.canonicalAttributeId]: { ...draft, value: event.target.value },
                              }))}
                              placeholder="Add value"
                            />
                          </td>
                          <td>
                            <input
                              className="mim-input"
                              value={draft.unit}
                              onChange={(event) => setDraftValues((current) => ({
                                ...current,
                                [row.canonicalAttributeId]: { ...draft, unit: event.target.value },
                              }))}
                              placeholder={row.canonicalUnit || 'Unit'}
                            />
                          </td>
                          <td>
                            <StatusPill status={row.compareMode || 'Exact'} tone="info" />
                          </td>
                          <td>
                            <div className="mim-toolbar" style={{ justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                className="mim-btn mim-btn-primary"
                                onClick={() => saveValueMutation.mutate(row)}
                                disabled={saveValueMutation.isPending || !draft.value.trim()}
                              >
                                Save
                              </button>
                              <button
                                type="button"
                                className="mim-btn mim-btn-secondary"
                                onClick={() => requestDeleteAttribute(row)}
                                disabled={deleteAttributeMutation.isPending}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </MimCard>

          {productId && (
            <details className="mim-collapse-section" open>
              <summary>Variant matrix (thickness / size)</summary>
              <VariantMatrixEditor
                productId={productId}
                onSaved={() => {
                  void comparisonQuery.refetch()
                }}
              />
            </details>
          )}
        </div>
      )}

      <Modal show={showFamilyModal} onHide={() => setShowFamilyModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Add attributes to {productFamily}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
            <div>
              <div className="fw-semibold">Master data attributes</div>
              <div className="text-muted small">Add reusable attributes to this product family.</div>
            </div>
            <Form.Control
              style={{ maxWidth: 300 }}
              value={masterTermSearch}
              onChange={(event) => setMasterTermSearch(event.target.value)}
              placeholder="Search master data"
            />
          </div>
          {availableMasterTerms.length === 0 ? (
            <div className="border rounded p-3 text-muted">No available master-data attributes found.</div>
          ) : (
            <div className="border rounded overflow-auto" style={{ maxHeight: 420 }}>
              {availableMasterTerms.map((term) => (
                <button
                  key={term.id}
                  type="button"
                  className={`w-100 text-start border-0 border-bottom bg-white p-3 ${selectedMasterTermIds.includes(term.id) ? 'table-primary' : ''}`}
                  onClick={() => toggleMasterTerm(term.id)}
                >
                  <div className="d-flex justify-content-between gap-3">
                    <div>
                      <div className="fw-semibold">{term.canonicalTerm}</div>
                      <div className="text-muted small">{term.aliases.slice(0, 3).map((alias) => alias.alias).join(', ') || 'No alternative names'}</div>
                    </div>
                    <Badge bg="light" text="dark">{term.category}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowFamilyModal(false)}>Cancel</Button>
          <Button
            onClick={() => addFamilyAttributesMutation.mutate()}
            disabled={addFamilyAttributesMutation.isPending || selectedMasterTermIds.length === 0}
          >
            {addFamilyAttributesMutation.isPending ? 'Adding...' : 'Add selected'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDirectModal} onHide={() => setShowDirectModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add product attribute</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack gap={3}>
            <div>
              <Form.Label>Attribute name</Form.Label>
              <Form.Control
                value={directForm.canonicalTerm}
                onChange={(event) => setDirectForm({ ...directForm, canonicalTerm: event.target.value })}
                placeholder="Thermal conductivity"
              />
            </div>
            <Row className="g-2">
              <Col md={7}>
                <Form.Label>Group</Form.Label>
                <Form.Select value={directForm.category} onChange={(event) => setDirectForm({ ...directForm, category: event.target.value })}>
                  {dataGroups.map((group) => <option key={group} value={group}>{group}</option>)}
                </Form.Select>
              </Col>
              <Col md={5}>
                <Form.Label>Data type</Form.Label>
                <Form.Select value={directForm.dataType} onChange={(event) => setDirectForm({ ...directForm, dataType: event.target.value })}>
                  {dataTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                </Form.Select>
              </Col>
            </Row>
            <Row className="g-2">
              <Col md={8}>
                <Form.Label>Value</Form.Label>
                <Form.Control
                  value={directForm.value}
                  onChange={(event) => setDirectForm({ ...directForm, value: event.target.value })}
                  placeholder="0.022"
                />
              </Col>
              <Col md={4}>
                <Form.Label>Unit</Form.Label>
                <Form.Control
                  value={directForm.unit}
                  onChange={(event) => setDirectForm({ ...directForm, unit: event.target.value })}
                  placeholder="W/mK"
                />
              </Col>
            </Row>
            <div>
              <Form.Label>Compare mode</Form.Label>
              <Form.Select
                value={directForm.compareMode}
                onChange={(event) => setDirectForm({
                  ...directForm,
                  compareMode: event.target.value as DirectAttributeForm['compareMode'],
                  useAiSecondOpinion: event.target.value === 'Semantic' ? true : directForm.useAiSecondOpinion,
                })}
              >
                <option value="Exact">Exact</option>
                <option value="Pattern">Pattern</option>
                <option value="Semantic">Semantic</option>
              </Form.Select>
            </div>
            <Form.Check
              type="switch"
              label="Use AI"
              checked={directForm.compareMode === 'Semantic' || directForm.useAiSecondOpinion}
              disabled={directForm.compareMode === 'Semantic'}
              onChange={(event) => setDirectForm({ ...directForm, useAiSecondOpinion: event.target.checked })}
            />
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDirectModal(false)}>Cancel</Button>
          <Button
            onClick={() => addDirectAttributeMutation.mutate()}
            disabled={addDirectAttributeMutation.isPending || !directForm.canonicalTerm.trim() || !directForm.value.trim()}
          >
            {addDirectAttributeMutation.isPending ? 'Adding...' : 'Add attribute'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
    </MimPage>
  )
}
